import { useEffect, useState } from "react";
import api from "@services/axios/apiManager";
import { buildXContextHeader } from "@utils/api/headers";
import { normalizePhone, normalizeTaxId } from "@utils/func/supplierIdentity";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import type { SupplierIdentity } from "@utils/store/cart/useOrder";

/**
 * 訂單詳情頁的「瓦斯行」資料（名稱／統編／電話）。
 *
 * 為什麼要另外抓：訂單 API（含快照）只帶 supplier_id 與 supplier_name，沒有統編與電話，
 * 而退換貨與取消政策要客戶「直接與瓦斯行聯繫」——沒有電話那句話就是空的。
 * 故以 bootstrap 的 available_suppliers 依 supplier_id 對應補齊。
 *
 * 限制：bootstrap 只回「這個客戶目前可用的瓦斯行」。歷史訂單的瓦斯行若已不在清單中
 * （客戶換過瓦斯行、該行已停止合作）就對不到 → 回 null，呼叫端只顯示訂單自帶的名稱，
 * 不顯示「統編 --」這種半殘資訊。長期解法是後端在訂單詳情帶上 supplier 快照欄位。
 *
 * @param supplierId 訂單所屬瓦斯行 id；未帶（尚未載入）時不發請求。
 * @param xContext 訂單詳情頁使用的 X-Context（「全部」分頁可能與當前身分不同）。
 */
export function useSupplierContact(supplierId?: string | null, xContext?: string | null) {
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const [contact, setContact] = useState<SupplierIdentity | null>(null);

  useEffect(() => {
    let canceled = false;
    if (!supplierId || !cusJwtToken) {
      setContact(null);
      return;
    }
    api
      .get("/app-api/platform-order-drafts/bootstrap", {
        headers: {
          Authorization: `Bearer ${cusJwtToken}`,
          "X-Context": xContext ?? buildXContextHeader(chooseIdentity),
        },
      })
      .then((res) => {
        if (canceled) return;
        const suppliers = res?.data?.data?.available_suppliers ?? [];
        const matched = suppliers.find((s: { supplier_id?: string }) => s.supplier_id === supplierId);
        const name = String(matched?.supplier_name ?? "").trim();
        if (!name) {
          setContact(null);
          return;
        }
        setContact({
          name,
          taxId: normalizeTaxId(matched?.tax_id_number),
          phone: normalizePhone(matched?.phone),
        });
      })
      .catch(() => {
        // 補充資訊而已，抓不到就只顯示訂單自帶的名稱，不打擾使用者
        if (!canceled) setContact(null);
      });
    return () => {
      canceled = true;
    };
  }, [supplierId, cusJwtToken, xContext, chooseIdentity]);

  return contact;
}
